import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useSeo, SITE_URL } from "../lib/seo";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { ShareButton } from "../components/ShareButton";
import { Pathways, DrugsToAvoid, InfoBlock, RiskBadge, MedicalDisclaimer } from "../components/clinical";
import { Button } from "../components/ui/button";
import { ArrowRight, ChevronLeft, FlaskConical, ListChecks, Loader2 } from "lucide-react";

function Glance({ rows }) {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="guide-glance">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="px-4 py-2.5">Procedure</th><th className="px-4 py-2.5">Initial assessment</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.procedure}>
              <td className="px-4 py-3 font-medium text-slate-800">{r.procedure}</td>
              <td className="px-4 py-3"><RiskBadge status={r.status} /><p className="text-xs text-slate-500 mt-1">{r.headline}</p></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cta() {
  return (
    <div className="rounded-2xl bg-[#0B2545] p-7 text-white" data-testid="guide-cta">
      <h2 className="font-heading text-lg font-semibold">Get a patient-specific decision in seconds</h2>
      <p className="text-slate-300 text-sm mt-1.5">Combine all conditions, medications, lab results and the planned procedure — with anaesthetic dose calculation, lab requests and a referral letter PDF.</p>
      <Link to="/register"><Button data-testid="guide-cta-btn" className="mt-4 bg-[#1C6FE3] hover:bg-[#1559c0]">Try the full assessment free <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
    </div>
  );
}

export default function GuideDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setG(null); setError(false);
    api.get(`/public/guides/${slug}`).then(({ data }) => setG(data)).catch((e) => setError(e.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  const description = g ? `${g.headline}. Emergency vs routine dental management, tests to request, drugs to avoid and references — ${g.name}.`.slice(0, 300) : "DentiCision clinical guide";
  useSeo({
    title: g ? `${g.title} | DentiCision` : "Clinical Guide | DentiCision",
    description, path: `/guides/${slug}`, noindex: !!error,
    jsonLd: g ? {
      "@context": "https://schema.org", "@type": "MedicalWebPage", name: g.title, url: `${SITE_URL}/guides/${slug}`, description,
      audience: { "@type": "MedicalAudience", audienceType: "Dentists" }, specialty: "Dentistry", lastReviewed: new Date().toISOString().slice(0, 10),
      about: g.kind === "medication" ? { "@type": "MedicalEntity", additionalType: "https://schema.org/Drug", name: g.name } : { "@type": "MedicalCondition", name: g.name },
      citation: g.references, publisher: { "@type": "Organization", name: "DentiCision", url: SITE_URL },
    } : null,
  });

  return (
    <div className="min-h-screen bg-white" data-testid="guide-detail-page">
      <PublicHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/guides" className="text-sm text-[#1C6FE3] inline-flex items-center gap-1" data-testid="guide-back-link"><ChevronLeft className="w-4 h-4" /> All clinical guides</Link>
        {error === "notfound" && <p className="mt-8 text-slate-600" data-testid="guide-not-found">Guide not found.</p>}
        {error === "error" && <p className="mt-8 text-slate-600" data-testid="guide-load-error">Could not load this guide. Please refresh the page.</p>}
        {!g && !error && <Loader2 className="w-6 h-6 animate-spin text-slate-400 mt-8" />}
        {g && (
          <article className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0B7569]">{g.category}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2545] mt-2 leading-tight" data-testid="guide-title">{g.title}</h1>
            <MedicalDisclaimer testid="guide-top-disclaimer" />
            <p className="text-base md:text-lg text-slate-700 mt-4 font-medium">{g.headline}</p>
            <p className="text-slate-600 mt-2 leading-relaxed">{g.rationale}</p>
            <div className="mt-5"><ShareButton path={`/guides/${slug}`} text={`${g.title} — free referenced guide by DentiCision:`} testid="guide-share-btn" /></div>

            <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10">Emergency vs routine management</h2>
            <Pathways pathways={g.pathways} id="guide" />
            <InfoBlock info={g.info} id="guide" />

            {g.modifications?.length > 0 && (
              <>
                <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10 flex items-center gap-2"><ListChecks className="w-5 h-5" /> Clinical precautions</h2>
                <ul className="mt-3 space-y-2">{g.modifications.map((m, i) => <li key={i} className="text-slate-700 text-sm leading-relaxed">• {m}</li>)}</ul>
              </>
            )}

            {g.scenarios?.length > 0 && (
              <section data-testid="guide-scenarios">
                <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10">Decision by clinical scenario</h2>
                <p className="text-sm text-slate-500 mt-1">{g.scenarios[0].question}</p>
                <div className="mt-3 space-y-3">
                  {g.scenarios.map((sc, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`guide-scenario-${i}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{sc.answer}</p>
                      <p className="font-semibold text-slate-900 mt-1">{sc.headline}</p>
                      <p className="text-sm text-slate-600 mt-1">{sc.rationale}</p>
                      <ul className="mt-2 space-y-1">{[...sc.routine, ...sc.modifications].slice(0, 4).map((m) => <li key={m} className="text-sm text-slate-700">• {m}</li>)}</ul>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {g.tests?.length > 0 && (
              <>
                <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10 flex items-center gap-2"><FlaskConical className="w-5 h-5" /> Information & tests to request</h2>
                <ul className="mt-3 space-y-2">{g.tests.map((t) => <li key={t.label} className="text-sm text-slate-700">• <b>{t.label}</b>{t.reason && <span className="text-slate-500"> — {t.reason}</span>}</li>)}</ul>
              </>
            )}

            <DrugsToAvoid drugs={g.drugs_to_avoid} id="guide" />

            <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10">At a glance by procedure</h2>
            <p className="text-sm text-slate-500 mt-1 mb-3">Initial assessment before lab results or timing details are entered.</p>
            <Glance rows={g.glance} />

            <div className="mt-10"><Cta /></div>

            <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10">References</h2>
            <ol className="mt-3 list-decimal pl-5 space-y-1 text-sm text-slate-600" data-testid="guide-references">{g.references.map((r) => <li key={r}>{r}</li>)}</ol>

            {g.related?.length > 0 && (
              <>
                <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] mt-10">Related guides</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  {g.related.map((r) => <Link key={r.slug} to={`/guides/${r.slug}`} className="text-sm rounded-full border border-slate-200 px-3 py-1.5 text-slate-700 hover:border-[#1C6FE3] hover:text-[#1C6FE3]">{r.name}</Link>)}
                </div>
              </>
            )}
            <MedicalDisclaimer testid="guide-medical-disclaimer" />
          </article>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
